import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, TrendingUp, FileText, Gauge, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";

const confColor = (c) => (c === "high" ? "#4b7a3a" : c === "medium" ? "#a3823f" : "#b23a48");

export default function ClientBbbForecast() {
  const [clients, setClients] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/predictions/client-bbb");
      setClients(data.clients || []);
    } catch {
      if (!silent) toast.error("Could not load client BBB forecast.");
      setClients([]);
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => { load(true); }, []);

  return (
    <div data-testid="client-bbb-forecast" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#c9a961]" strokeWidth={1.8} />
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#1e3d3a]/60">Client BBB forecast</span>
          <span className="text-[11px] text-[#1e3d3a]/45">· built from CRM + Expense AI activity</span>
        </div>
        <button
          type="button"
          data-testid="client-bbb-refresh"
          onClick={() => load(false)}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-50 transition-colors duration-300"
        >
          {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
        </button>
      </div>
      <p className="mt-1 text-sm text-[#1e3d3a]/65 max-w-2xl">
        Each client's current BBB rating vs. where they're headed — the more documents you file in their CRM folder and the stronger their Expense AI health, the higher the projection.
      </p>

      {clients === null && (
        <div className="py-8 flex items-center justify-center gap-2 text-[#1e3d3a]/50">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading forecast…
        </div>
      )}

      {clients?.length === 0 && (
        <div data-testid="client-bbb-empty" className="mt-4 rounded-xl border border-dashed border-[#1e3d3a]/20 bg-[#eae7de]/40 p-8 text-center text-sm text-[#1e3d3a]/55">
          Upload documents into a client's CRM folder or run an Expense AI analysis, and their current → predicted BBB forecast will build here automatically.
        </div>
      )}

      {clients?.length > 0 && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
          {clients.map((c) => {
            const same = c.current_bbb === c.projected_bbb;
            return (
              <div key={c.client_id} data-testid={`client-bbb-card-${c.client_id}`} className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-head text-base text-[#1e3d3a] truncate">{c.business_name}</h4>
                    <p className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/45 truncate">{c.client_id}</p>
                  </div>
                  <span
                    className="shrink-0 rounded-full px-2.5 py-1 text-[9px] uppercase tracking-[0.16em] font-semibold"
                    style={{ color: confColor(c.confidence), background: confColor(c.confidence) + "18" }}
                    title="Forecast confidence"
                  >
                    {c.confidence}
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 rounded-lg border border-[#1e3d3a]/10 bg-white px-3 py-2.5">
                    <div className="text-[8px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">
                      Current BBB{c.current_estimated ? " · est." : ""}
                    </div>
                    <div className="mt-0.5 font-head text-2xl font-light text-[#1e3d3a]" data-testid={`client-bbb-current-${c.client_id}`}>
                      {c.current_bbb || "—"}
                    </div>
                  </div>
                  <span className="text-[#c9a961] text-lg font-light">→</span>
                  <div className={`flex-1 rounded-lg border px-3 py-2.5 ${c.projected_bbb && !same ? "border-[#c9a961]/45 bg-[#c9a961]/10" : "border-[#1e3d3a]/10 bg-white"}`}>
                    <div className="text-[8px] uppercase tracking-[0.2em] text-[#a3823f]">Predicted</div>
                    <div className="mt-0.5 font-head text-2xl font-light text-[#1e3d3a]" data-testid={`client-bbb-projected-${c.client_id}`}>
                      {c.projected_bbb || "—"}
                      {c.projected_bbb && !same && (
                        <span className="ml-2 align-middle text-[9px] uppercase tracking-[0.18em] text-[#4b7a3a]">
                          {c.step_change < 0 ? "At risk" : "Uplift"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#1e3d3a]/60">
                  {c.health_score != null && (
                    <span className="inline-flex items-center gap-1"><Gauge className="h-3 w-3 text-[#a3823f]" /> Health {Math.round(c.health_score)}/100</span>
                  )}
                  <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3 text-[#a3823f]" /> {c.crm_doc_count} CRM · {c.expense_count} analysis</span>
                  {c.has_progress && <span>· {c.completion_pct}% milestones</span>}
                </div>

                <p className="mt-2 text-[11px] leading-relaxed text-[#1e3d3a]/55">{c.explanation}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
